import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Intent, NonIdealState, Tag } from '@blueprintjs/core';

import { Button } from 'components/buttons';
import { ShowHide } from 'components/layout';
import { ListingTable } from 'components/listing';
import { EmployeeCircleStatus } from 'components/status';
import { Tooltip } from 'components/tooltip';

import {
    formatAccount,
    formatCounterpart,
    formatDriverName,
    formatGap,
    formatMode,
    formatSide,
    formatSignedOff,
    formatUtcDateTime,
    formatValue,
    provisionalTagText,
    readFailedHeading,
    readFailedMessage,
    retryText,
    untriagedText
} from './diffViewerFormatting';

const pageSize = 100;
const evidenceChangedText = "Evidence changed since it was signed off";

const tableHeaders = [
    "Driver",
    "Reference",
    "Account",
    "Mode",
    "Occurred at (UTC)",
    "Side",
    "Legacy value",
    "RE2 value",
    "Evidence",
    "Label",
    ""
];

const defaultArray = [];
const defaultFunction = () => null;

// Ordered by driver and then by time, so one driver's differences read as a run.
function sortByDriverThenTime(differences) {
    return [...differences].sort((first, second) => {
        const byName = formatValue(first.driver.name).localeCompare(formatValue(second.driver.name));

        if (byName !== 0) {
            return byName;
        }

        if (first.driver.employeeId !== second.driver.employeeId) {
            return first.driver.employeeId - second.driver.employeeId;
        }

        return formatValue(first.occurredAt).localeCompare(formatValue(second.occurredAt));
    });
}

function buildLabelCell(difference) {
    const autoLabel = difference.autoLabel;
    const signOff = difference.signOff;
    const previousSignOff = difference.previousSignOff;

    return (
        <div key="label">
            <ShowHide
                evaluator={autoLabel != null}
                show={(
                    <Fragment>
                        <Tag intent={Intent.PRIMARY} minimal>{autoLabel?.text}</Tag>
                        <p className="break-word">{formatValue(autoLabel?.rootCause)}</p>
                        <ShowHide
                            evaluator={autoLabel?.source != null}
                            show={(<p>{`Source: ${autoLabel?.source}`}</p>)}
                        />
                    </Fragment>
                )}
            />
            <ShowHide
                evaluator={signOff != null}
                show={(
                    <Fragment>
                        <Tag intent={Intent.SUCCESS} minimal>{signOff?.labelText}</Tag>
                        <p className="break-word">{formatValue(signOff?.reason)}</p>
                        <p>{signOff == null ? "" : formatSignedOff(signOff)}</p>
                        <ShowHide
                            evaluator={signOff?.source != null}
                            show={(<p>{`Source: ${signOff?.source}`}</p>)}
                        />
                        <ShowHide
                            evaluator={signOff?.workItemId != null}
                            show={(<p>{`Work item ${signOff?.workItemId}`}</p>)}
                        />
                    </Fragment>
                )}
            />
            <ShowHide
                evaluator={autoLabel == null && signOff == null}
                show={(<span>{untriagedText}</span>)}
            />
            <ShowHide
                evaluator={difference.evidenceChanged && previousSignOff != null}
                show={(
                    <div id={`rules-engine-diff-evidence-changed-${difference.id}`}>
                        <Tag intent={Intent.WARNING} minimal>{evidenceChangedText}</Tag>
                        <p>{`Previously signed off as ${formatValue(previousSignOff?.labelText)}`}</p>
                        <p className="break-word">{formatValue(previousSignOff?.reason)}</p>
                        <p>{previousSignOff == null ? "" : formatSignedOff(previousSignOff)}</p>
                        <ShowHide
                            evaluator={previousSignOff?.workItemId != null}
                            show={(<p>{`Work item ${previousSignOff?.workItemId}`}</p>)}
                        />
                    </div>
                )}
            />
            <ShowHide
                evaluator={difference.isProvisional}
                show={(<Tag intent={Intent.WARNING} minimal>{provisionalTagText}</Tag>)}
            />
        </div>
    );
}

function buildEvidenceCell(difference) {
    return (
        <div key="evidence">
            <ShowHide
                evaluator={difference.secondsDifference != null}
                show={(<p>{formatGap(difference)}</p>)}
            />
            <p>{formatCounterpart(difference)}</p>
        </div>
    );
}

/**
 * The rows of the open group. Every value either engine produced is on the row, so a difference can
 * be judged here without opening anything else. A failed read is shown as such, never as an empty group.
 */
export function DiffViewerDifferences(
    {
        differences = defaultArray,
        page = 1,
        loading = false,
        failed = false,
        blockedReason = defaultFunction,
        onPageChange = defaultFunction,
        onOpenInHarness = defaultFunction,
        onSignOff = defaultFunction,
        onRetry = defaultFunction
    }
) {
    const sortedDifferences = sortByDriverThenTime(differences);
    // A page past the end, as a hand-edited link can ask for, shows the last page rather than nothing.
    const lastPage = Math.max(1, Math.ceil(sortedDifferences.length / pageSize));
    const shownPage = Math.min(page, lastPage);
    const firstRow = (shownPage - 1) * pageSize;

    function buildOpenButton(difference) {
        const reason = blockedReason(difference.driver.accountId);

        const button = (
            <Button
                id={`rules-engine-diff-open-${difference.id}`}
                text="Open in harness"
                icon="play"
                large={false}
                disabled={reason != null}
                onClick={() => onOpenInHarness(difference)}
            />
        );

        if (reason == null) {
            return button;
        }

        return (
            <Tooltip content={reason}>
                {button}
            </Tooltip>
        );
    }

    function buildActionsCell(difference) {
        return (
            <div className="inline-items" key="actions">
                {buildOpenButton(difference)}
                <Button
                    id={`rules-engine-diff-sign-off-${difference.id}`}
                    text={difference.signOff == null ? "Sign off" : "Change sign-off"}
                    icon="endorsed"
                    large={false}
                    onClick={() => onSignOff(difference)}
                />
            </div>
        );
    }

    function buildRow(difference) {
        return [
            (
                <div className="inline-items no-spacing" key="driver">
                    <EmployeeCircleStatus
                        status={difference.driver.statusName}
                        tooltip={difference.driver.statusTooltip}
                        isAgency={difference.driver.isAgency}
                    />
                    <span className="break-word">{formatDriverName(difference.driver)}</span>
                </div>
            ),
            (<span className="break-word" key="reference">{difference.driver.reference}</span>),
            formatAccount(difference.driver),
            formatMode(difference.mode),
            formatUtcDateTime(difference.occurredAt),
            formatSide(difference.side),
            (<span className="break-word" key="legacy">{formatValue(difference.legacyValue)}</span>),
            (<span className="break-word" key="under-test">{formatValue(difference.underTestValue)}</span>),
            buildEvidenceCell(difference),
            buildLabelCell(difference),
            buildActionsCell(difference)
        ];
    }

    function onPagingChange(newPage) {
        onPageChange(newPage);
    }

    return (
        <ShowHide
            evaluator={failed}
            show={(
                <div className="list-table-no-results" id="rules-engine-diff-group-failed">
                    <NonIdealState
                        title={readFailedHeading}
                        description={readFailedMessage}
                        action={(
                            <Button id="rules-engine-diff-group-retry" text={retryText} icon="refresh" onClick={onRetry} />
                        )}
                    />
                </div>
            )}
            hide={(
                <div className="wide-table">
                    <ListingTable
                        id="listing-table-rules-engine-diffs"
                        headers={tableHeaders}
                        data={sortedDifferences.slice(firstRow, firstRow + pageSize).map(buildRow)}
                        totalRecordCount={sortedDifferences.length}
                        loadingData={loading}
                        startPage={shownPage}
                        pageSize={pageSize}
                        showPerPage={false}
                        onPagingChange={onPagingChange}
                        noDataHeading="No differences in this group"
                        noDataMessage="Nothing in this group matches the filters any more."
                    />
                </div>
            )}
        />
    );
}

DiffViewerDifferences.propTypes = {
    differences: PropTypes.array,
    page: PropTypes.number,
    loading: PropTypes.bool,
    failed: PropTypes.bool,
    blockedReason: PropTypes.func,
    onPageChange: PropTypes.func,
    onOpenInHarness: PropTypes.func,
    onSignOff: PropTypes.func,
    onRetry: PropTypes.func
};
